import _concatInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/concat';
import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import _slicedToArray from '@babel/runtime-corejs3/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime-corejs3/helpers/esm/objectWithoutProperties';
import React, { useRef, useState, useReducer, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import scrollIntoView from 'scroll-into-view-if-needed';
import Dropdown from '@webteam/dropdown';
import { List, ListItem } from '@webteam/list';
import { withTheme } from '@webteam/ui-contexts';
import { convertToOptionGroupsIfNeeded, getEmptyMessage, shouldOpenSelect } from '../utils.js';
import { optionType, noOptionsMessageType } from '../common-prop-types.js';
import { multiselectReducer, getInitialState } from '../state/multiselect/reducer.js';
import { setGroups, setCursorIndex, closeMenu, openMenu, cursorMovedDown, cursorMovedUp, clearSelectedAndCloseMenu } from '../state/select/actions.js';
import { setSelectedValue, setHideSelected, addSelectedIndex, removeSelectedIndex, removeSelectedOption } from '../state/multiselect/actions.js';
import { getOptionValue, isIndexMatchCursor, getCursorIndex } from '../state/select/selectors.js';
import { isEmpty, getSelectedArray, getPlainOptions, isSelected, getGroups } from '../state/multiselect/selectors.js';
import { ESCAPE, BACKSPACE, ARROW_DOWN, ARROW_UP, ENTER, TAB, SPACE } from '../key-codes.js';
import SelectTrigger from './trigger.js';
import FormikTriggerWrapper from './formik-trigger-wrapper.js';
import UnselectOptionHint from './unselect-option-hint.js';

function Multiselect(_ref) {
  var options = _ref.options,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'm' : _ref$size,
      theme = _ref.theme,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? null : _ref$className,
      _ref$defaultValue = _ref.defaultValue,
      defaultValue = _ref$defaultValue === void 0 ? null : _ref$defaultValue,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? undefined : _ref$value,
      onChange = _ref.onChange,
      _ref$placeholder = _ref.placeholder,
      placeholder = _ref$placeholder === void 0 ? 'Placeholder' : _ref$placeholder,
      _ref$isSingleLine = _ref.isSingleLine,
      isSingleLine = _ref$isSingleLine === void 0 ? false : _ref$isSingleLine,
      _ref$isClearable = _ref.isClearable,
      isClearable = _ref$isClearable === void 0 ? false : _ref$isClearable,
      _ref$isLoading = _ref.isLoading,
      isLoading = _ref$isLoading === void 0 ? false : _ref$isLoading,
      _ref$isDisabled = _ref.isDisabled,
      isDisabled = _ref$isDisabled === void 0 ? false : _ref$isDisabled,
      noOptionsMessage = _ref.noOptionsMessage,
      _ref$unselectHintText = _ref.unselectHintText,
      unselectHintText = _ref$unselectHintText === void 0 ? 'Unselect item' : _ref$unselectHintText,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'bottom' : _ref$placement,
      dataTest = _ref['data-test'],
      restProps = _objectWithoutProperties(_ref, ["options", "size", "theme", "className", "defaultValue", "value", "onChange", "placeholder", "isSingleLine", "isClearable", "isLoading", "isDisabled", "noOptionsMessage", "unselectHintText", "placement", "data-test"]);

  var dropdownRef = useRef(null);
  var highlightedItemRef = React.createRef();

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isKeyboardNavigating = _useState2[0],
      setIsKeyboardNavigating = _useState2[1];

  var _useReducer = useReducer(multiselectReducer, getInitialState(convertToOptionGroupsIfNeeded(options), defaultValue, !isSingleLine)),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1];

  useEffect(function () {
    dispatch(setGroups(convertToOptionGroupsIfNeeded(options)));
  }, [options]);
  useEffect(function () {
    if (value !== undefined) {
      dispatch(setSelectedValue(value));
    }
  }, [value]);
  useEffect(function () {
    dispatch(setHideSelected(!isSingleLine));
  }, [isSingleLine]);
  var isEmptyList = isEmpty(state);
  var unselectHint = React.createElement(UnselectOptionHint, {
    text: unselectHintText,
    size: size,
    theme: theme
  });
  var listItems = renderOptionList(state, unselectHint, isSingleLine, highlightedItemRef, toggleSelectIndex, function (i) {
    if (!isIndexMatchCursor(state, i)) {
      dispatch(setCursorIndex(i));
    }
  });
  var preventHoverIfNeeded = isKeyboardNavigating ? {
    pointerEvents: 'none'
  } : {};
  useEffect(function () {
    if (dropdownRef.current) {
      dropdownRef.current.scheduleUpdate();
    }
  }, [state.searchString, state.selected]);
  var handleMouseMove = useCallback(function () {
    return setIsKeyboardNavigating(false);
  }, []);
  useEffect(function () {
    if (isKeyboardNavigating) {
      document.addEventListener('mousemove', handleMouseMove);
    }

    return function () {
      return document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseMove, isKeyboardNavigating]);
  useEffect(function () {
    if (isKeyboardNavigating && highlightedItemRef.current) {
      var elementToScrollTo = highlightedItemRef.current;

      if (state.cursorIndex[0] === 0 && state.cursorIndex[1] === 0) {
        var groupHeader = highlightedItemRef.current.previousSibling;

        if (groupHeader) {
          elementToScrollTo = groupHeader;
        }
      }

      scrollIntoView(elementToScrollTo, {
        behavior: 'instant',
        scrollMode: 'if-needed',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [highlightedItemRef, state.cursorIndex, isKeyboardNavigating]);

  function handleClose() {
    dispatch(closeMenu());
    setIsKeyboardNavigating(false);
  }

  function toggleSelectIndex(index) {
    var selectedOption = getGroups(state)[index[0]].options[index[1]];
    var optionAdded = !isSelected(state, getOptionValue(selectedOption));
    var toggleSelectAction = optionAdded ? addSelectedIndex(index) : removeSelectedIndex(index);
    dispatch(toggleSelectAction);
    setIsKeyboardNavigating(false);
    callOnChangeIfNeeded(onChange, state, toggleSelectAction);
  } // eslint-disable-next-line complexity


  function handleTriggerKeyDown(e) {
    if (e.key === ESCAPE) {
      handleClose();
      e.preventDefault();
    }

    if (isDisabled) {
      return;
    }

    if (state.menuIsOpen) {
      handleKeyNavigation(e);
    } else {
      if (shouldOpenSelect(e.key)) {
        dispatch(openMenu());
        e.preventDefault();
      }

      if (e.key === BACKSPACE && isClearable) {
        handleClearClick();
        e.preventDefault();
      }
    }
  } // eslint-disable-next-line complexity


  function handleKeyNavigation(e) {
    if (e.key === ARROW_DOWN || e.key === ARROW_UP) {
      var isDownDirection = e.key === ARROW_DOWN;
      dispatch(isDownDirection ? cursorMovedDown() : cursorMovedUp());
      setIsKeyboardNavigating(true);
      e.preventDefault();
    }

    if (e.key === ENTER || e.key === TAB || e.key === SPACE) {
      var cursorIndex = getCursorIndex(state);

      if (cursorIndex !== null) {
        toggleSelectIndex(cursorIndex);
      } else {
        handleClose();
      }

      e.preventDefault();
    }
  }

  function handleClick() {
    if (isDisabled) {
      return;
    }

    var action = state.menuIsOpen ? closeMenu() : openMenu();
    dispatch(action);
  }

  function handleClearClick() {
    var action = clearSelectedAndCloseMenu();
    dispatch(action);
    callOnChangeIfNeeded(onChange, state, action);
  }

  function handleUnselectClick(option) {
    var action = removeSelectedOption(option);
    dispatch(action);
    callOnChangeIfNeeded(onChange, state, action);
  }

  return React.createElement(Dropdown, {
    size: size,
    theme: theme,
    onRequestClose: handleClose,
    borderHardness: isEmptyList ? 'pale' : 'average',
    widthEqualsTriggers: true,
    watchWindowResize: true,
    placement: placement,
    ref: dropdownRef,
    trigger: function trigger(_ref2) {
      var ref = _ref2.ref;
      return React.createElement(FormikTriggerWrapper, {
        className: className,
        theme: theme,
        size: size,
        error: restProps.error,
        label: restProps.label,
        note: restProps.note,
        "data-test": dataTest
      }, React.createElement(SelectTrigger, {
        theme: theme,
        size: size,
        isOpen: state.menuIsOpen,
        triggerRef: ref,
        onKeyDown: handleTriggerKeyDown,
        selected: value ? value : getSelectedArray(state),
        placeholder: placeholder,
        isClearable: isClearable,
        isLoading: isLoading,
        isDisabled: isDisabled,
        isSingleLine: isSingleLine,
        onClick: handleClick,
        onClearClick: handleClearClick,
        onItemClearClick: handleUnselectClick,
        error: Boolean(restProps.error)
      }));
    },
    isOpen: state.menuIsOpen
  }, React.createElement(List, {
    "data-test": "option-list",
    size: size,
    theme: theme,
    style: preventHoverIfNeeded,
    iconPlacement: 'right'
  }, !isEmptyList ? listItems : React.createElement(ListItem, {
    disabled: true
  }, getEmptyMessage(isLoading, false, noOptionsMessage))));
}

Multiselect.propTypes = {
  className: PropTypes.string,
  options: PropTypes.arrayOf(optionType),
  defaultValue: PropTypes.arrayOf(optionType),
  value: PropTypes.arrayOf(optionType),
  onChange: PropTypes.func,
  size: PropTypes.oneOf(['m', 's']),
  theme: PropTypes.oneOf(['light', 'dark']),
  placeholder: PropTypes.string,
  isSingleLine: PropTypes.bool,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
  label: PropTypes.node,
  note: PropTypes.node,
  noOptionsMessage: noOptionsMessageType,
  unselectHintText: PropTypes.string,
  placement: PropTypes.oneOf(['auto-start', 'auto', 'auto-end', 'top-start', 'top', 'top-end', 'right-start', 'right', 'right-end', 'bottom-end', 'bottom', 'bottom-start', 'left-end', 'left', 'left-start']),
  'data-test': PropTypes.string
};

function renderOptionList(state, unselectHint, isSingleLine, highlightedItemRef, toggleSelectIndex, handleHighlightIndex) {
  var _context;

  return _mapInstanceProperty(_context = getPlainOptions(state)).call(_context, function (item) {
    var _context2;

    var index = item.index,
        label = item.label;
    var value = getOptionValue(item);
    var isItemSelected = isSelected(state, value);
    var isHighlighted = isIndexMatchCursor(state, index);
    var icon = isSingleLine && isIndexMatchCursor(state, index) && isItemSelected ? unselectHint : null;
    return React.createElement(ListItem, {
      key: _concatInstanceProperty(_context2 = "".concat(value, "_")).call(_context2, index[0]),
      selected: isItemSelected,
      highlighted: isHighlighted,
      ref: isHighlighted ? highlightedItemRef : null,
      onMouseMove: function onMouseMove() {
        return handleHighlightIndex(index);
      },
      onClick: function onClick() {
        return toggleSelectIndex(index);
      },
      icon: icon
    }, label);
  });
}

function callOnChangeIfNeeded(onChange, state, action) {
  if (onChange) {
    onChange(getSelectedArray(multiselectReducer(state, action)));
  }
}

var multiselect = withTheme(Multiselect);

export { multiselect as default };
